import { useState } from 'react';
import { C } from '../../lib/theme';
import { styles } from '../../lib/styles';
import { UNSUCCESSFUL_REASONS } from '../../domain/constants';
import type { Case } from '../../domain/initial-cases';
import { Dialog } from '../Dialog';
import { useAppData } from '../../state/contexts/AppDataContext';
import { useUIState } from '../../state/contexts/UIStateContext';
import { getCurrentUser } from '../../auth/useUser';

// H-6: reason codes and notes live in local state until Confirm — Cancel
// must leave the case untouched, and the whole decision lands as ONE PATCH
// (one audit row) instead of one per checkbox/keystroke.
export function UnsuccessfulDecisionModal({ c }: { c: Case }) {
  const { updateCase } = useAppData();
  const { setModal } = useUIState();
  const [reasonCodes, setReasonCodes] = useState<string[]>(c.reasonCodes || []);
  const [notes, setNotes] = useState(c.notes || '');
  return (
    <Dialog title="Mark as Unsuccessful Vet" onClose={() => setModal(null)}>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Select applicable reason codes (per BCID Best Practices):</div>
        {UNSUCCESSFUL_REASONS.map((r, i) => (
          <div key={i} style={{ ...styles.checkItem, marginBottom: 4 }}>
            <input type="checkbox" id={`reason-${i}`} checked={reasonCodes.includes(r)} onChange={e => {
              setReasonCodes(prev => e.target.checked ? [...prev, r] : prev.filter(x => x !== r));
            }} />
            <label htmlFor={`reason-${i}`} style={{ fontSize: 12 }}>{r}</label>
          </div>
        ))}
        <div style={{ marginTop: 12 }}>
          <label htmlFor="unsuccessful-notes" style={{ fontSize: 11, color: C.textMuted }}>Additional Notes</label>
          <textarea id="unsuccessful-notes" style={styles.textarea} value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button style={{ ...styles.btn, ...styles.btnDanger }} onClick={() => {
            // #587 — the SECOND time this modal can be reached, and the cure it
            // closes. A cured case (`cureStatus: 'CURED'` with
            // `status: 'IN_REVIEW'`) is the one decided case DecisionTab offers
            // its controls on again, and Mark: Unsuccessful is one of the two
            // outcomes of that re-decision. The cure ends here whichever way it
            // goes, so `cureStatus` moves to CLOSED in the SAME PATCH as the
            // decision — H-6's rule that the whole decision is one write and one
            // audit row, applied to the field this issue added.
            //
            // Symmetric with the approve path in DecisionTab.tsx, deliberately:
            // one path writing CLOSED and the other leaving CURED would make
            // "was this cure resolved?" depend on which way it went.
            //
            // The pair `CURED` + `IN_REVIEW` is the test, not `cureStatus`
            // alone, because that is what DecisionTab gates its controls on and
            // two answers to "is this case back in vetting?" is the drift #574
            // and #579 each spent an issue removing.
            const reDeciding = c.cureStatus === 'CURED' && c.status === 'IN_REVIEW';
            // #629 — THE SECOND UNSUCCESSFUL NOTICE, and the reason this write
            // is what re-opens the path to it.
            //
            // A registrant told their vetting failed a second time is owed a
            // second notice: new reason codes, and a new opportunity to cure.
            // #587 shipped without one and the case dead-ended. Not only Send
            // Notification, which is gated on `!notificationSentAt` and so
            // vanished the moment the FIRST notice was recorded — Record Cure,
            // Record Appeal and Close (No Response) all need an OPEN cure
            // window, Issue Final needs `status: 'APPEAL'`, and the Decision tab
            // is correctly read-only again. Every exit was shut at once.
            //
            // WHAT THIS CLEARS, and it is the whole fix: the notification tuple
            // and nothing else. `notificationSentAt`, `notificationMethod` and
            // `notificationDeadline` are written together by `sendNotification`
            // (state/actions/lifecycle.ts) and are cleared together here, so the
            // case comes out of this write in exactly the shape a FIRST
            // unsuccessful decision has before its notice: decided, notifiable,
            // not yet notified. Every surface then reads it correctly with no
            // change of its own — Send Notification reappears, the 30-day
            // countdown re-arms against the NEW `decisionDate`, the Cases
            // queue's Notification column says "Pending" again, the API's
            // `notification=pending` filter counts it, and
            // `undeliveredNoticeReason` returns null at its first line rather
            // than describing a superseded notice as this case's.
            //
            // WHY THE CURE WINDOW IS NOT RE-OPENED HERE. `cureStatus` still
            // moves to CLOSED, as #587 wrote it, because the 12 business days
            // run from the NOTICE and not from the decision: `sendNotification`
            // is the one place that stamps `cureDeadline` and flips `cureStatus`
            // to OPEN, and it already does both unconditionally on an
            // UNSUCCESSFUL decision. So the second notice opens the second
            // window by the same code path as the first, and the regained exits
            // come back at the right moment — when the registrant has actually
            // been told, not when the analyst pressed a button. Writing
            // `cureStatus: 'OPEN'` here instead would put the case on the Open
            // Cure Windows queue with a deadline from the superseded notice,
            // start the SLA scan's cure clock (workers/handlers/sla-scan.ts,
            // scoped to OPEN) against a date nobody has been given, and split
            // "when does a cure window open?" into two answers.
            //
            // `cureDeadline` is likewise left alone rather than nulled: it is
            // the date the first notice named, `undeliveredNoticeReason`
            // documents it as fixed at notification time, and the second notice
            // overwrites it in the same PATCH that opens the second window. Its
            // pairing with `cureStatus` — the invariant sla-scan.ts's header
            // rests on, that nothing moves one without the other — is untouched.
            //
            // THE SUPERSEDED NOTICE IS KEPT, in this decision's audit detail.
            // The first notice's own NOTIFICATION_SENT and CURE_WINDOW_OPENED
            // rows are already in `audit_trail` and append-only, so the history
            // is not at risk; what clearing the columns would lose is the tie
            // between the notice being retired and the decision that retired
            // it. `supersededNotice` records the three values as they stood, so
            // the audit trail answers "which notice did this decision replace,
            // and what date was the registrant given?" from one row.
            //
            // Gated on there BEING a notice, not on `reDeciding` alone: a cured
            // case with no recorded notification has nothing to clear and
            // nothing to supersede, and writing three nulls plus an empty detail
            // would claim otherwise.
            const supersededNotice = reDeciding && c.notificationSentAt
              ? {
                  sentAt: c.notificationSentAt,
                  method: c.notificationMethod ?? null,
                  cureDeadline: c.cureDeadline ?? null,
                }
              : null;
            // #776 — THE APPROVER, on this path too. The approve path in
            // DecisionTab has always written `approverId`; this one never did,
            // so a denial recorded nobody as having decided it — no name for
            // the Decision and Overview tabs to show, and nothing for the API's
            // approver ≠ recommender check to compare. The Keycloak sub, as
            // there: the API resolves it to users.id (CLAUDE.md B-2).
            const user = getCurrentUser()!;
            updateCase(c.id, {
              reasonCodes, notes,
              decision: 'UNSUCCESSFUL', decisionDate: new Date().toISOString(), status: 'UNSUCCESSFUL',
              approverId: user.id,
              ...(reDeciding ? { cureStatus: 'CLOSED' } : {}),
              ...(supersededNotice ? { notificationSentAt: null, notificationMethod: null, notificationDeadline: null } : {}),
              action: { code: 'DECISION_UNSUCCESSFUL', detail: {
                reasonCodeCount: reasonCodes.length, reasonCodes,
                ...(reDeciding ? { afterCure: true } : {}),
                ...(supersededNotice ? { supersededNotice } : {}),
              } },
            });
            setModal(null);
          }}>Confirm Unsuccessful</button>
          <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={() => setModal(null)}>Cancel</button>
        </div>
    </Dialog>
  );
}
